import React, { useEffect, useRef, useState } from 'react'
import classnames from 'classnames/bind'
import styles from './styles.scss'

const cx = classnames.bind(styles)

const DEFAULT_MAX_LENGTH = 500

export interface NotesSectionProps {
  /** Current notes value */
  notes: string
  /** Called to commit the notes (fires on blur, not on every keystroke) */
  onChange: (notes: string) => void
  /** Placeholder text. Default: '' */
  placeholder?: string
  /** Max characters allowed. Default: 500 */
  maxLength?: number
  /** Automation ID */
  elmId?: string
}

/**
 * NotesSection — Free-text textarea for the "Anything else?" section.
 *
 * Edits are kept locally and committed on blur (so the consumer's autosave
 * fires once when the user leaves the field, not on every keystroke). The
 * parent value only syncs in while the field is not focused, to avoid
 * clobbering in-progress edits.
 *
 * @example
 * <NotesSection
 *   notes={criteria.notes}
 *   onChange={(val) => actions.updateNotes(val)}
 * />
 */
const NotesSection: React.FC<NotesSectionProps> = ({
  notes,
  onChange,
  placeholder = '',
  maxLength = DEFAULT_MAX_LENGTH,
  elmId = 'buying-criteria-notes-textarea',
}) => {
  const [value, setValue] = useState(notes)
  const isFocusedRef = useRef(false)

  useEffect(() => {
    if (!isFocusedRef.current) {
      setValue(notes)
    }
  }, [notes])

  const handleBlur = () => {
    isFocusedRef.current = false
    if (value !== notes) {
      onChange(value)
    }
  }

  return (
    <div className={cx('notes-field')}>
      <textarea
        data-elm-id={elmId}
        className={cx('notes-textarea')}
        value={value}
        maxLength={maxLength}
        placeholder={placeholder}
        onFocus={() => {
          isFocusedRef.current = true
        }}
        onChange={e => setValue(e.target.value)}
        onBlur={handleBlur}
      />
      <span className={cx('notes-counter')} data-elm-id={`${elmId}-counter`}>
        {value.length}/{maxLength}
      </span>
    </div>
  )
}

export default NotesSection
